import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useMyTeamInvitations } from '../../hooks/useMyTeamInvitations';
import { cn } from '../../lib/utils';
import { TeamSwitcher } from './TeamSwitcher';
import { PlanBadge, StatusLabel, Eyebrow } from '../Common';
import { formatRoleName } from '../../utils/formatters';

const UserMenu: React.FC = () => {
  const { user, signOut } = useAuth();
  const { userAccount, activeTeam, teamMemberships, isStaff, isSuperadmin } = useUser();
  const { subscription } = useSubscription();
  const { invitations, refetch: refetchInvitations } = useMyTeamInvitations();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = userAccount?.display_name || user?.email;
  const avatarInitial = (displayName || '?').trim().charAt(0).toUpperCase() || '?';
  const hasBillingWarning = subscription && (subscription.billingHealth === 'payment_issue' || subscription.billingHealth === 'canceling');
  const showTeamSwitcher = isStaff || isSuperadmin || teamMemberships.length > 1;
  const hasInvitations = invitations.length > 0;

  // One dot, highest-severity wins: money at risk outranks an invitation
  // waiting to be accepted. Navy reads as "something to look at", not alarm.
  const triggerDotColor =
    subscription?.billingHealth === 'payment_issue' ? 'var(--critical-700)'
    : subscription?.billingHealth === 'canceling' ? 'var(--caution-700)'
    : hasInvitations ? 'var(--navy-700)'
    : null;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refetch on open so invitations sent mid-session appear without a reload.
  // Nothing is needed for the accept/decline round trip: `/invitations/:token`
  // is a standalone full-screen route that renders no nav, so this component
  // unmounts on the way in and remounts on the way out — and the remount
  // refetches. (Verified in prod: no nav element on that route.)
  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) refetchInvitations();
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
  };

  const planId = activeTeam?.plan_id || teamMemberships[0]?.plan_id;
  const roleId = activeTeam?.role_id || teamMemberships[0]?.role_id;

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger */}
      <button
        onClick={handleToggle}
        className="relative flex items-center gap-2 py-1 pl-1 pr-2 rounded text-ink-700 hover:bg-paper-2 transition-colors"
      >
        <span
          className="inline-flex items-center justify-center rounded-full bg-navy-700 font-serif flex-shrink-0"
          style={{ width: 28, height: 28, color: 'var(--paper)', fontSize: 14, lineHeight: 1 }}
        >
          {avatarInitial}
        </span>
        <span className="font-sans" style={{ fontSize: 12.5 }}>{displayName}</span>
        {hasInvitations && (
          <span className="sr-only">
            {invitations.length === 1
              ? '1 pending team invitation'
              : `${invitations.length} pending team invitations`}
          </span>
        )}
        <ChevronDown
          size={13}
          strokeWidth={1.25}
          className={cn('text-ink-500 transition-transform duration-200', isOpen && 'rotate-180')}
        />
        {triggerDotColor && (
          <span
            className="absolute -top-0.5 -right-0.5 rounded-full"
            style={{ width: 8, height: 8, background: triggerDotColor }}
          />
        )}
      </button>

      {/* Dropdown card */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 z-[1000] w-72 bg-ivory rounded-lg animate-fade-in-up"
          style={{
            border: '1px solid var(--stone-300)',
            boxShadow: '0 8px 24px rgba(26,31,42,0.10), 0 2px 6px rgba(26,31,42,0.06)',
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-stone-200">
            <div className="text-sm font-medium text-ink-900 truncate">{displayName}</div>
            {userAccount?.display_name && user?.email && (
              <div className="text-xs text-ink-500 truncate mt-0.5">{user.email}</div>
            )}
            {/* Plan + role badges */}
            {(planId || roleId) && (
              <div className="flex items-center gap-1.5 mt-2">
                <PlanBadge planId={planId} size="xs" />
                <span className="text-xs text-ink-500">
                  {formatRoleName(roleId)}
                </span>
              </div>
            )}
          </div>

          {/* Team switcher */}
          {showTeamSwitcher && (
            <div className="px-4 py-2.5 border-b border-stone-200">
              <TeamSwitcher hideBadges />
            </div>
          )}

          {/* Pending invitations awaiting this user */}
          {hasInvitations && (
            <div className="px-4 py-2.5 border-b border-stone-200">
              <Eyebrow>
                {invitations.length === 1 ? 'Team invitation' : `${invitations.length} team invitations`}
              </Eyebrow>
              <div className="flex flex-col gap-1 mt-1.5">
                {invitations.map(invitation => (
                  <Link
                    key={invitation.invitationId}
                    to={`/invitations/${invitation.token}`}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 -mx-2 px-2 py-1.5 rounded no-underline hover:bg-paper-2 transition-colors"
                  >
                    <span
                      className="rounded-full flex-shrink-0"
                      style={{ width: 6, height: 6, background: 'var(--navy-700)' }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-ink-900 truncate">
                        Join {invitation.teamName}
                      </span>
                      <span className="block text-xs text-ink-500">
                        as {formatRoleName(invitation.roleId)}
                      </span>
                    </span>
                    <ChevronRight size={13} strokeWidth={1.25} className="text-ink-500 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-sm text-ink-900 no-underline hover:bg-paper-2 transition-colors"
            >
              Profile & Settings
            </Link>
            {hasBillingWarning && (
              <Link
                to="/profile?tab=subscription"
                onClick={() => setIsOpen(false)}
                className="flex items-center px-4 py-2 no-underline hover:bg-paper-2 transition-colors"
              >
                <StatusLabel tone={subscription.billingHealth === 'payment_issue' ? 'critical' : 'caution'}>
                  {subscription.billingHealth === 'payment_issue' ? 'Payment issue' : 'Plan expires soon'}
                </StatusLabel>
              </Link>
            )}
          </div>

          {/* Sign out */}
          <div className="border-t border-stone-200 py-1">
            <button
              onClick={handleSignOut}
              className="w-full text-left px-4 py-2 text-sm text-claret-700 hover:bg-paper-2 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
